import React, { useState, useEffect } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { UserProfile } from '../../types';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: UserProfile;
  onComplete: (updates: Partial<UserProfile>) => void;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
  profile,
  onComplete
}) => {
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [bio, setBio] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && profile) {
      setName(profile.displayName || '');
      setHandle(profile.handle || '');
      setBio(profile.bio || '');
    }
  }, [isOpen, profile]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const safeHandle = handle || '';
      
      const updates: Partial<UserProfile> = {
        displayName: name,
        handle: safeHandle.startsWith('@') ? safeHandle : `@${safeHandle}`,
        bio
      };
      
      onComplete(updates);
      onClose();
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex flex-col items-center justify-end lg:justify-center p-0 lg:p-6 bg-black/90 backdrop-blur-md animate-in fade-in duration-300">
      
      {/* 🟢 CONTAINER: Smooth bottom sheet on mobile (rounded-t-[2rem]), sharp box on desktop (lg:rounded-none) */}
      <div className="w-full h-[90vh] lg:h-auto lg:max-w-2xl bg-black lg:border border-zinc-800 flex flex-col relative animate-in slide-in-from-bottom lg:slide-in-from-bottom-0 duration-300 rounded-t-[2rem] lg:rounded-none shadow-2xl overflow-hidden">
        
        {/* HEADER */}
        <div className="p-6 lg:p-8 flex justify-between items-center bg-black z-10 border-b border-zinc-800 sticky top-0">
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors lg:hidden">
            <ArrowLeft size={24} />
          </button>
          
          <h2 className="text-2xl lg:text-4xl font-black text-white uppercase italic tracking-tighter">
            Edit Profile
          </h2>
          
          <button onClick={onClose} className="hidden lg:block text-zinc-500 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        
        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-10 space-y-8 bg-black">
          
          {/* NAME INPUT */}
          <div>
            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3 block pl-2 lg:pl-0">
              Display Name
            </label>
            {/* 🟢 INPUT: curved on mobile, square on desktop */}
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl lg:rounded-none p-4 text-white font-black uppercase tracking-wider outline-none focus:border-white transition-colors"
              placeholder="ENTER YOUR NAME"
            />
          </div>
          
          {/* HANDLE INPUT */}
          <div>
            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3 block pl-2 lg:pl-0">
              Username
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-4 text-zinc-600 font-mono font-bold">@</span>
              <input
                type="text"
                value={(handle || '').replace('@', '')}
                onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl lg:rounded-none p-4 pl-10 text-white font-mono text-sm outline-none focus:border-white transition-colors placeholder:uppercase placeholder:font-sans placeholder:font-black placeholder:tracking-wider"
                placeholder="USERNAME"
              />
            </div>
          </div>
          
          {/* BIO INPUT */}
          <div>
            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3 block pl-2 lg:pl-0">
              Bio
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl lg:rounded-none p-4 text-white font-mono text-sm leading-relaxed outline-none focus:border-white h-40 resize-none transition-colors"
              placeholder="TRANSMIT THOUGHTS..."
              maxLength={200}
            />
            <div className="flex justify-between items-center mt-3 px-2 lg:px-0">
               <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">
                  Keep it strictly culture.
               </span>
               <span className="text-[10px] font-mono text-zinc-500">
                  {bio?.length || 0} / 200
               </span>
            </div>
          </div>
        </div>

        {/* FOOTER ACTION */}
        <div className="p-6 border-t border-zinc-800 bg-black flex justify-end pb-safe">
           {/* 🟢 BUTTON: Pill on mobile, sharp block on desktop */}
           <button 
             onClick={handleSave} 
             disabled={loading || !name.trim()}
             className={`px-8 py-4 font-black uppercase tracking-[0.2em] text-xs transition-all flex items-center justify-center w-full lg:w-auto rounded-full lg:rounded-none ${
               loading || !name.trim() 
               ? 'bg-zinc-900 text-zinc-600 cursor-not-allowed border border-zinc-800' 
               : 'bg-white text-black hover:bg-zinc-200 shadow-xl hover:scale-105'
             }`}
           >
             {loading ? 'Transmitting...' : 'Save Profile'}
           </button>
        </div>

      </div>
    </div>
  );
};